import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Languages } from 'lucide-react';
import { clearValueLabelCache } from '../hooks/useValueLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The pick-lists whose *values* an applicant sees on the public form.
const LISTS = [
  ['education_levels', 'Education levels'],
  ['occupations', 'Occupations / trades'],
  ['attachment_categories', 'Document types'],
  ['relationships', 'Emergency-contact relationships'],
  ['countries', 'Countries'],
  ['electrical_cert_types', 'Certificates · Electrical'],
  ['scissor_lift_cert_types', 'Certificates · Scissor lift'],
  ['boom_lift_cert_types', 'Certificates · Boom lift'],
  ['osha_cert_types', 'Certificates · OSHA'],
  ['health_safety_cert_types', 'Certificates · Health & safety'],
  ['eu_visa_types', 'EU visas / permits'],
  ['us_visa_types', 'US visas'],
  ['uk_work_permit_types', 'UK work permits'],
];

/**
 * Polish captions for list *values*. The stored value stays English so
 * reports, filters and exports keep working — only what a Polish reader
 * sees on the application form changes.
 */
export default function ValueTranslationsSettings({ canEdit }) {
  const { t } = useTranslation();
  const [labels, setLabels] = useState({});
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tr, st] = await Promise.all([
        axios.get(`${API}/settings/value-translations`),
        axios.get(`${API}/settings`),
      ]);
      setLabels(tr.data?.pl || {});
      setSettings(st.data || {});
    } catch { toast.error(t('auto.misc2.couldNotLoadThePolishCaptions')); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    if (!settings) return [];
    return LISTS
      .map(([key, title]) => ({
        key,
        title,
        values: (settings[key] || []).filter(v => typeof v === 'string' && v.trim()),
      }))
      .filter(g => g.values.length > 0);
  }, [settings]);

  const missingCount = useMemo(
    () => groups.reduce((n, g) => n + g.values.filter(v => !labels[v]).length, 0),
    [groups, labels],
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/value-translations`, { lang: 'pl', labels });
      // Let the rest of the app pick the new captions up without a reload.
      clearValueLabelCache();
      toast.success(t('auto.misc2.polishCaptionsSaved'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="value-translations-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <Languages className="w-4 h-4" /> {t('auto.misc2.polishCaptionsForListValues')}
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          What a Polish-speaking applicant sees in the dropdowns on the application form.
          The value saved into the app stays English, so reports and filters are unaffected.
          {missingCount > 0 && ` ${missingCount} value(s) still have no Polish caption.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={onlyMissing}
            onChange={(e) => setOnlyMissing(e.target.checked)}
            data-testid="value-translations-only-missing"
          />
          {t('auto.misc2.showOnlyTheOnesWithoutA')}
        </label>

        {groups.map((g) => {
          const rows = onlyMissing ? g.values.filter(v => !labels[v]) : g.values;
          if (!rows.length) return null;
          return (
            <div key={g.key} className="space-y-2">
              <p className="text-sm font-medium text-slate-800">{g.title}</p>
              <div className="space-y-1.5">
                {rows.map((value) => (
                  <div key={value} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
                    <span className="text-xs text-slate-600 truncate" title={value}>{value}</span>
                    <Input
                      className="h-8 text-sm"
                      value={labels[value] || ''}
                      placeholder={t('auto.misc2.polishCaption')}
                      disabled={!canEdit}
                      onChange={(e) => setLabels(prev => ({ ...prev, [value]: e.target.value }))}
                      data-testid={`value-translation-${g.key}-${value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="value-translations-save">
              {saving ? t('auto.misc2.saving2') : t('auto.misc2.savePolishCaptions')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
